import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import { IncotermsResponseOutDTO, LovDTO } from '@api/ringil-api.v2.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { Field } from '@components/framework/formfield/Field.tsx';
import { ApiContext } from '@api/api.tsx';
import { InputText } from 'primereact/inputtext';
import MultipleValues from '@components/ringil3/HelperComponents/MultipleValues.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import { stampPrimeDropdownOverlayTestIds } from '@utils/testUtils';

interface ComponentProps {
  secured?: SecuredConfig;
  value: IncotermsResponseOutDTO;
  required?: boolean;
  readonly?: boolean;
  onChange: (value: IncotermsResponseOutDTO) => void;
  dataTestId?: string;
  dataTestIdList?: string;
  dataTestIdInput?: string;
  dataTestIdSave?: string;
  dataTestIdClose?: string;
}

const IncotermsTag: React.FC<ComponentProps> = ({ secured,
  readonly,
  required,
  value,
  onChange,
  dataTestId,
  dataTestIdList,
  dataTestIdInput,
  dataTestIdSave,
  dataTestIdClose,
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const [incoterms, setIncoterms] = useState<LovDTO[]>([]);
  const { tr } = useTranslationLgs();
  const { cuLoVIncotermsValues } = useContext(ApiContext);

  const applyOverlayTestIds = () => stampPrimeDropdownOverlayTestIds(dataTestIdList);

  useEffect(() => {
    cuLoVIncotermsValues(d => {
      setIncoterms(d.filter(item => item.active));
    });
  }, [])

  const renderButtoTagValue = () => {
    if (value) return <MultipleValues
      values={[value.incotermsValue, value.incotermsDescription]}></MultipleValues>;
    return null;
  };

  return (
    <>
      <ButtonTag secured={secured}
        label={tr('IncotermsTag.title', 'Incoterms')}
        required={required}
        readonly={readonly}
        value={renderButtoTagValue()}
        onSave={(onSuccess) => {
          onChange(internalValue);
          onSuccess();
        }}
        popupWidth={'400px'}
        autoClose
        dataTestId={dataTestId}
        dataTestIdSave={dataTestIdSave}
        dataTestIdClose={dataTestIdClose}
      >

        <>
          <Field label={tr('IncotermsTag.incoterms', 'Incoterms')}>
            <div data-testid={dataTestIdList}>
              <Dropdown
                value={internalValue?.incotermsValue}
                className="w-full"
                options={incoterms}
                showClear={true}
                optionLabel="name"
                optionValue="id"
                panelClassName={dataTestIdList ? 'expeditionInfoDropdownPanel' : undefined}
                onShow={dataTestIdList ? () => requestAnimationFrame(applyOverlayTestIds) : undefined}
                onChange={e =>
                  setInternalValue(draft => {
                    if (!draft) return { incotermsValue: e.target.value };
                    if (isNullOrUndefined(e.target.value) || e.target.value === '') return { incotermsDescription: null, incotermsValue: null };
                    return { ...draft, incotermsValue: e.target.value };
                  })
                }
              />
            </div>

          </Field>

          <Field label={tr("IncotermsTag.description", "Incoterms description")} disabled={!internalValue?.incotermsValue}>
            <InputText
              value={internalValue?.incotermsValue ? internalValue.incotermsDescription ?? '' : ''}
              onChange={e => setInternalValue(draft => {
                if (!draft) return { incotermsDescription: e.target.value };
                return { ...draft, incotermsDescription: e.target.value };
              })}
              disabled={!internalValue?.incotermsValue}
              data-testid={dataTestIdInput}
            />
          </Field>
        </>
      </ButtonTag>
    </>);

};

export default IncotermsTag;